import { FC, useState, useEffect } from 'react';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { Box, StatusIndicator, TextButton } from '@wix/design-system';
import { useIntersectionObserver } from 'usehooks-ts';
import { InfiniteScrollProps, ReviewsWithInfiniteScrollProps } from './types';
import ReviewsList from './ReviewsContent/ReviewsList';

const ReviewsWithInfiniteScroll = ({
  reviews,
  reviewsCount,
  averageReviewRating,
  i18nKeys,
  isMobileView,
  replyProps,

  isMyProfile,
  services,
  handleBiGenericClick,
  onEdit,
  userReview,

  borderContainerClass,
  listProps,

  cursor,
  fetchMoreOnScroll,
  fetchNextReviews,
  ...rest
}: ReviewsWithInfiniteScrollProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { isIntersecting, ref } = useIntersectionObserver({});
  useEffect(() => {
    if (!isLoading && isIntersecting && cursor && fetchMoreOnScroll) {
      setIsLoading(true);
      fetchNextReviews()
        .then(() => setIsLoading(false))
        .catch(() => setIsLoading(false));
    }
  }, [isIntersecting, isLoading, cursor, fetchMoreOnScroll]);

  return (
    <>
      <Box dataHook="reviews-container" display="block">
        <ReviewsList
          reviews={reviews}
          reviewsCount={reviewsCount}
          averageReviewRating={averageReviewRating}
          i18nKeys={i18nKeys}
          isMobileView={isMobileView}
          replyProps={replyProps}
          isMyProfile={isMyProfile}
          services={services}
          handleBiGenericClick={handleBiGenericClick}
          onEdit={onEdit}
          userReview={userReview}
          {...listProps}
          gridProps={{
            ...listProps?.gridProps,
            numberOfColumns: 1, // Always force 1 column for infinite scroll
          }}
          {...rest}
        />
        {!fetchMoreOnScroll && reviewsCount > reviews.length && (
          <ShowMoreButton fetchNextReviews={fetchNextReviews} />
        )}
      </Box>

      {cursor && fetchMoreOnScroll && (
        <div ref={ref} data-hook="infiniteScroll-container">
          <Box marginTop="26px" align="center" verticalAlign="middle">
            <StatusIndicator status="loading" />
          </Box>
        </div>
      )}
    </>
  );
};

const ShowMoreButton: FC<Pick<InfiniteScrollProps, 'fetchNextReviews'>> = ({
  fetchNextReviews,
}) => {
  const { t } = useTranslation();
  return (
    <Box paddingTop="30px" paddingBottom="42px">
      <TextButton underline="always" skin="dark" onClick={fetchNextReviews}>
        {t('show-more-text-button')}
      </TextButton>
    </Box>
  );
};

export default ReviewsWithInfiniteScroll;
